import React, { useEffect, useState, useCallback, useMemo } from "react";
import StaffShell from "@/components/layout/StaffShell";
import { staffApi, formatApiError, formatIDR, CURRENCY_OPTIONS } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { hasPermission } from "@/lib/permissions";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Copy, Search, X, ChevronLeft, ChevronRight, ArrowUp, ArrowDown, GitBranch } from "lucide-react";
import ImageUpload from "@/components/ImageUpload";

const empty = {
  name: "",
  description: "",
  image_url: null,
  allow_online_book: true,
  allow_gift_certificate: false,
  branch_ids: [],
  items: [],
  pricing: { same_for_all: true, price: 0, per_branch: {} },
  active: true,
};

export default function BrandPackages() {
  const { staff } = useAuth();
  const canCreate = hasPermission(staff, "brand.packages.create");
  const canEdit = hasPermission(staff, "brand.packages.edit");
  const canDelete = hasPermission(staff, "brand.packages.delete");
  const [items, setItems] = useState([]);
  const [services, setServices] = useState([]);
  const [products, setProducts] = useState([]);
  const [seriesTemplates, setSeriesTemplates] = useState([]);
  const [branches, setBranches] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [step, setStep] = useState(1);

  // Compact filter + pagination state (mirrors Brand → Services).
  const [nameFilter, setNameFilter] = useState("");
  const [serviceFilter, setServiceFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(() => {
    const v = parseInt(localStorage.getItem("brand-pkg-rpp") || "25", 10);
    return [10, 25, 50, 100].includes(v) ? v : 25;
  });
  useEffect(() => { localStorage.setItem("brand-pkg-rpp", String(rowsPerPage)); }, [rowsPerPage]);
  useEffect(() => { setPage(1); }, [nameFilter, serviceFilter, statusFilter, rowsPerPage]);

  const load = useCallback(async () => {
    try {
      const [p, s, b, pr, se] = await Promise.all([
        staffApi.get("/packages"),
        staffApi.get("/services", { params: { active_only: false } }),
        staffApi.get("/branches"),
        staffApi.get("/products"),
        staffApi.get("/series-templates"),
      ]);
      setItems(p.data);
      setServices(s.data);
      setBranches(b.data);
      setProducts(pr.data || []);
      setSeriesTemplates(se.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const svcMap = useMemo(() => Object.fromEntries(services.map((s) => [s.id, s])), [services]);
  const catalogMaps = useMemo(() => ({
    service: svcMap,
    product: Object.fromEntries(products.map((p) => [p.id, p])),
    series: Object.fromEntries(seriesTemplates.map((s) => [s.id, s])),
  }), [svcMap, products, seriesTemplates]);
  const priceOf = (s) => Number(s?.pricing?.price ?? s?.price ?? 0);

  const filteredRows = useMemo(() => {
    const n = nameFilter.trim().toLowerCase();
    return items.filter((p) => {
      if (n && !(p.name || "").toLowerCase().includes(n)) return false;
      if (serviceFilter !== "all" && !(p.service_ids || []).includes(serviceFilter)) return false;
      if (statusFilter === "active" && !p.active) return false;
      if (statusFilter === "inactive" && p.active) return false;
      return true;
    });
  }, [items, nameFilter, serviceFilter, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / rowsPerPage));
  const clampedPage = Math.min(page, totalPages);
  const pagedRows = useMemo(
    () => filteredRows.slice((clampedPage - 1) * rowsPerPage, clampedPage * rowsPerPage),
    [filteredRows, clampedPage, rowsPerPage]
  );
  const resetFilters = () => { setNameFilter(""); setServiceFilter("all"); setStatusFilter("all"); };

  const hydrate = (p) => {
    // Convert a legacy or wizard-shape package into the wizard's canonical form.
    const items = Array.isArray(p.items) && p.items.length
      ? p.items.map((it) => ({
          item_type: it.item_type || "service",
          ref_id: it.ref_id || it.service_id,
          service_id: (it.item_type || "service") === "service" ? (it.ref_id || it.service_id) : null,
          substitutions: it.substitutions || [],
        }))
      : (p.service_ids || []).map((id) => ({ item_type: "service", ref_id: id, service_id: id, substitutions: [] }));
    const pricing = p.pricing && typeof p.pricing === "object"
      ? { same_for_all: p.pricing.same_for_all !== false, price: Number(p.pricing.price ?? p.price ?? 0), per_branch: p.pricing.per_branch || {} }
      : { same_for_all: true, price: Number(p.price ?? 0), per_branch: {} };
    return {
      ...empty,
      ...p,
      items,
      pricing,
      branch_ids: p.branch_ids || [],
      description: p.description || "",
      image_url: p.image_url || null,
      allow_online_book: p.allow_online_book !== false,
      allow_gift_certificate: !!p.allow_gift_certificate,
      active: p.active !== false,
    };
  };
  const openNew = () => { setEditing(null); setForm(empty); setStep(1); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm(hydrate(p)); setStep(1); setOpen(true); };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Package name is required");
    if (form.items.length === 0) return toast.error("Add at least one package item on Step 3");
    const payload = {
      name: form.name.trim(),
      description: form.description || "",
      image_url: form.image_url || null,
      allow_online_book: !!form.allow_online_book,
      allow_gift_certificate: !!form.allow_gift_certificate,
      branch_ids: form.branch_ids || [],
      items: form.items.map((it) => ({
        item_type: it.item_type || "service",
        ref_id: it.ref_id || it.service_id,
        service_id: (it.item_type || "service") === "service" ? (it.ref_id || it.service_id) : null,
        substitutions: it.substitutions || [],
      })),
      pricing: {
        same_for_all: !!form.pricing.same_for_all,
        price: parseFloat(form.pricing.price || 0),
        per_branch: Object.fromEntries(
          Object.entries(form.pricing.per_branch || {}).map(([bid, v]) => [
            bid, { price: parseFloat(v.price || 0), currency: v.currency || "IDR" },
          ])
        ),
      },
      active: !!form.active,
    };
    try {
      if (editing) await staffApi.put(`/packages/${editing.id}`, payload);
      else await staffApi.post("/packages", payload);
      toast.success("Package saved");
      setOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const clone = async (p) => {
    try { await staffApi.post(`/packages/${p.id}/clone`); toast.success("Cloned"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}"?`)) return;
    try { await staffApi.delete(`/packages/${p.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <StaffShell context="brand">
      <div className="flex items-end justify-between mb-4">
        <div>
          <p className="tracking-overline text-xs text-muted-foreground">BRAND CATALOG</p>
          <h1 className="text-3xl font-semibold mt-1" data-testid="packages-title">Packages</h1>
          <p className="text-sm text-muted-foreground">Combine multiple services into one visit or bundle. E.g. &ldquo;Snow White&rdquo; = Botox + Facial + Massage.</p>
        </div>
        {canCreate && (
          <Button onClick={openNew} data-testid="new-package-btn" className="gap-1"><Plus className="w-4 h-4" /> New package</Button>
        )}
      </div>

      <Card className="rounded-lg mb-3">
        <CardContent className="p-3">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[220px] max-w-sm">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={nameFilter}
                onChange={(e) => setNameFilter(e.target.value)}
                placeholder="Search package name…"
                className="pl-8 h-8 text-sm"
                data-testid="pkg-name-filter"
              />
            </div>
            <Select value={serviceFilter} onValueChange={setServiceFilter}>
              <SelectTrigger className="h-8 text-xs w-[220px]" data-testid="pkg-service-filter">
                <span className="text-muted-foreground mr-1">Includes:</span>
                <SelectValue placeholder="Any service" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any service</SelectItem>
                {services.map((s) => (<SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>))}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="h-8 text-xs w-[120px]" data-testid="pkg-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
            {(nameFilter || serviceFilter !== "all" || statusFilter !== "all") && (
              <Button variant="ghost" size="sm" onClick={resetFilters} className="h-8 gap-1 text-xs" data-testid="pkg-filter-reset">
                <X className="w-3.5 h-3.5" /> Reset
              </Button>
            )}
            <div className="ml-auto text-xs text-muted-foreground" data-testid="pkg-result-count">
              Showing <span className="font-medium text-foreground">{filteredRows.length === 0 ? 0 : (clampedPage - 1) * rowsPerPage + 1}
              –{Math.min(clampedPage * rowsPerPage, filteredRows.length)}</span> of <span className="font-medium text-foreground">{filteredRows.length}</span>
              {filteredRows.length !== items.length && <> · <span className="text-muted-foreground">{items.length} total</span></>}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="rounded-lg">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="h-8">
                <TableHead className="py-1.5 text-[11px] uppercase tracking-wider">Name</TableHead>
                <TableHead className="py-1.5 text-[11px] uppercase tracking-wider">Services</TableHead>
                <TableHead className="py-1.5 text-[11px] uppercase tracking-wider">Package price</TableHead>
                <TableHead className="py-1.5 text-[11px] uppercase tracking-wider">À la carte</TableHead>
                <TableHead className="py-1.5 text-[11px] uppercase tracking-wider">Savings</TableHead>
                <TableHead className="py-1.5 text-[11px] uppercase tracking-wider">Status</TableHead>
                <TableHead className="py-1.5 text-[11px] uppercase tracking-wider text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pagedRows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-xs text-muted-foreground py-8" data-testid="pkg-empty-row">
                    {items.length === 0 ? "No packages yet — click ‘New package’ to combine services." : "No packages match your filters."}
                  </TableCell>
                </TableRow>
              )}
              {pagedRows.map((p) => {
                const pkgItems = (p.items || []).length
                  ? (p.items || []).map((it) => ({ type: it.item_type || "service", ref: it.ref_id || it.service_id }))
                  : (p.service_ids || []).map((id) => ({ type: "service", ref: id }));
                const sum = pkgItems.reduce((a, x) => a + priceOf(catalogMaps[x.type]?.[x.ref]), 0);
                const price = Number(p?.pricing?.price ?? p.price ?? 0);
                const savings = sum - price;
                const perBranch = p?.pricing?.same_for_all === false;
                return (
                  <TableRow key={p.id} className="h-9 hover:bg-muted/40" data-testid={`package-row-${p.id}`}>
                    <TableCell className="font-medium py-1.5 text-sm">
                      <div className="flex items-center gap-2 min-w-0">
                        {p.image_url && (
                          <img
                            src={p.image_url.startsWith("http") ? p.image_url : `${window.location.origin}${p.image_url}`}
                            alt=""
                            className="w-6 h-6 rounded object-cover flex-shrink-0"
                          />
                        )}
                        <span className="truncate">{p.name}</span>
                      </div>
                    </TableCell>
                    <TableCell className="py-1.5 text-xs text-muted-foreground max-w-[280px]">
                      <span className="truncate block">{pkgItems.map((x) => catalogMaps[x.type]?.[x.ref]?.name).filter(Boolean).join(" · ") || "—"}</span>
                    </TableCell>
                    <TableCell className="py-1.5 text-xs whitespace-nowrap">{formatIDR(price)}{perBranch ? " *" : ""}</TableCell>
                    <TableCell className="py-1.5 text-xs whitespace-nowrap text-muted-foreground">{formatIDR(sum)}</TableCell>
                    <TableCell className="py-1.5 text-xs whitespace-nowrap">
                      {savings > 0 ? <span className="text-primary font-medium">−{formatIDR(savings)}</span> : "—"}
                    </TableCell>
                    <TableCell className="py-1.5">
                      {p.active
                        ? <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 rounded-full border border-emerald-200 h-5 px-2 text-[10px]">Active</Badge>
                        : <Badge variant="secondary" className="rounded-full h-5 px-2 text-[10px]">Inactive</Badge>}
                    </TableCell>
                    <TableCell className="py-1 text-right">
                      <div className="inline-flex items-center gap-0">
                        {canEdit && <Button size="icon" variant="ghost" onClick={() => openEdit(p)} className="h-7 w-7" data-testid={`edit-package-${p.id}`} title="Edit"><Pencil className="w-3.5 h-3.5" /></Button>}
                        {canCreate && <Button size="icon" variant="ghost" onClick={() => clone(p)} className="h-7 w-7" data-testid={`clone-package-${p.id}`} title="Duplicate"><Copy className="w-3.5 h-3.5" /></Button>}
                        {canDelete && <Button size="icon" variant="ghost" onClick={() => remove(p)} className="h-7 w-7 text-destructive hover:text-destructive" data-testid={`delete-package-${p.id}`} title="Delete"><Trash2 className="w-3.5 h-3.5" /></Button>}
                        {!canEdit && !canCreate && !canDelete && <span className="text-muted-foreground italic text-xs pr-2">Read-only</span>}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          {filteredRows.length > 0 && (
            <div className="flex items-center justify-between px-3 py-2 border-t border-border text-xs" data-testid="pkg-pagination">
              <div className="flex items-center gap-2 text-muted-foreground">
                <span>Rows per page</span>
                <Select value={String(rowsPerPage)} onValueChange={(v) => setRowsPerPage(parseInt(v, 10))}>
                  <SelectTrigger className="h-7 w-[70px] text-xs" data-testid="pkg-rows-per-page"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {[10, 25, 50, 100].map((n) => (<SelectItem key={n} value={String(n)}>{n}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground">Page {clampedPage} of {totalPages}</span>
                <Button size="icon" variant="outline" className="h-7 w-7" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={clampedPage <= 1} data-testid="pkg-page-prev"><ChevronLeft className="w-3.5 h-3.5" /></Button>
                <Button size="icon" variant="outline" className="h-7 w-7" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={clampedPage >= totalPages} data-testid="pkg-page-next"><ChevronRight className="w-3.5 h-3.5" /></Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <PackageWizard
        open={open}
        editing={editing}
        step={step}
        setStep={setStep}
        form={form}
        setForm={setForm}
        services={services}
        products={products}
        seriesTemplates={seriesTemplates}
        branches={branches}
        onClose={() => setOpen(false)}
        onSave={save}
      />
    </StaffShell>
  );
}

/* --------------- 4-step wizard --------------- */
// Mirrors the Brand → Services wizard flow but tuned for packages:
//   1. Details (name / description / online-book / gift-cert / image)
//   2. Locations (branch multi-select with check-all / uncheck-all)
//   3. Package Items (searchable service picker + Substitutions + Move/Delete)
//   4. Pricing (single price or per-branch price+currency)
const STEP_TITLES = ["Details", "Locations", "Package Items", "Pricing & Deposit"];

function PackageWizard({ open, editing, step, setStep, form, setForm, services, products, seriesTemplates, branches, onClose, onSave }) {
  const canNext = () => {
    if (step === 1) return !!form.name.trim();
    if (step === 2) return true;
    if (step === 3) return form.items.length > 0;
    return true;
  };
  const next = () => setStep((s) => Math.min(4, s + 1));
  const back = () => setStep((s) => Math.max(1, s - 1));

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[86vh] overflow-y-auto" data-testid="pkg-wizard">
        <DialogHeader>
          <DialogTitle>
            {editing ? "Edit" : "New"} Package: {STEP_TITLES[step - 1]} <span className="text-sm text-muted-foreground font-normal">(Step {step} of 4)</span>
          </DialogTitle>
          <DialogDescription>
            Configure the package&apos;s details, availability, items and pricing across the 4 steps below.
          </DialogDescription>
        </DialogHeader>

        <WizardStepper step={step} />

        {step === 1 && <Step1Details form={form} setForm={setForm} />}
        {step === 2 && <Step2Locations form={form} setForm={setForm} branches={branches} />}
        {step === 3 && <Step3Items form={form} setForm={setForm} services={services} products={products} seriesTemplates={seriesTemplates} />}
        {step === 4 && <Step4Pricing form={form} setForm={setForm} branches={branches} services={services} products={products} seriesTemplates={seriesTemplates} />}

        <div className="mt-4 flex items-center justify-between border-t border-border pt-3">
          <div className="flex items-center gap-2">
            {step > 1 && (
              <Button variant="ghost" onClick={back} data-testid="pkg-wizard-back">Back</Button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={onClose} data-testid="pkg-wizard-cancel">Cancel</Button>
            {step < 4 ? (
              <Button onClick={next} disabled={!canNext()} data-testid="pkg-wizard-next">Continue</Button>
            ) : (
              <Button onClick={onSave} data-testid="pkg-wizard-save">Save package</Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function WizardStepper({ step }) {
  return (
    <div className="flex items-center gap-2 mb-4" data-testid="pkg-wizard-stepper">
      {[1, 2, 3, 4].map((n) => (
        <React.Fragment key={n}>
          <div className={
            "flex items-center gap-2 text-xs " +
            (n === step ? "text-foreground font-medium" : (n < step ? "text-muted-foreground" : "text-muted-foreground/50"))
          }>
            <span className={
              "w-6 h-6 rounded-full flex items-center justify-center text-[11px] border " +
              (n === step ? "bg-primary text-primary-foreground border-primary" : (n < step ? "bg-muted border-border" : "border-border"))
            }>
              {n}
            </span>
            <span>{STEP_TITLES[n - 1]}</span>
          </div>
          {n < 4 && <div className="flex-1 h-px bg-border" />}
        </React.Fragment>
      ))}
    </div>
  );
}

/* ---------- Step 1: Details ---------- */
function Step1Details({ form, setForm }) {
  return (
    <div className="grid gap-4">
      <div>
        <Label htmlFor="pkg-name">Package name</Label>
        <Input id="pkg-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Snow White" className="mt-1" data-testid="pkg-name-input" />
      </div>
      <div>
        <Label htmlFor="pkg-desc">Description</Label>
        <Textarea id="pkg-desc" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1" data-testid="pkg-desc-input" />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="rounded-lg border border-border p-3">
          <div className="flex items-center justify-between">
            <Label className="text-sm">Allow customers to book online</Label>
            <Switch checked={form.allow_online_book} onCheckedChange={(v) => setForm({ ...form, allow_online_book: v })} data-testid="pkg-online-book-switch" />
          </div>
          <p className="text-[11px] text-muted-foreground mt-1">If off, this package is only bookable by staff (POS / calendar).</p>
        </div>
        <div className="rounded-lg border border-border p-3">
          <div className="flex items-center justify-between">
            <Label className="text-sm">Allow sale on Gift Card</Label>
            <Switch checked={form.allow_gift_certificate} onCheckedChange={(v) => setForm({ ...form, allow_gift_certificate: v })} data-testid="pkg-gift-switch" />
          </div>
          <p className="text-[11px] text-muted-foreground mt-1">Front-desk can create an invoice with this treatment name (not a nominal amount).</p>
        </div>
      </div>
      <div>
        <Label>Image</Label>
        <p className="text-[11px] text-muted-foreground mb-1">Shown on the online menu.</p>
        <ImageUpload value={form.image_url} onChange={(url) => setForm({ ...form, image_url: url })} label="Package image" testid="pkg-image-upload" />
      </div>
      <div className="flex items-center gap-2">
        <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="pkg-active-switch" />
        <Label>Active</Label>
      </div>
    </div>
  );
}

/* ---------- Step 2: Locations ---------- */
function Step2Locations({ form, setForm, branches }) {
  const allIds = branches.map((b) => b.id);
  const toggle = (id) => setForm({
    ...form,
    branch_ids: form.branch_ids.includes(id) ? form.branch_ids.filter((x) => x !== id) : [...form.branch_ids, id],
  });
  const checkAll = () => setForm({ ...form, branch_ids: allIds });
  const uncheckAll = () => setForm({ ...form, branch_ids: [] });
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <p className="text-sm text-muted-foreground flex-1">Which branches offer this package?</p>
        <button type="button" onClick={checkAll} className="text-xs text-primary hover:underline" data-testid="pkg-check-all">Check all</button>
        <span className="text-muted-foreground text-xs">|</span>
        <button type="button" onClick={uncheckAll} className="text-xs text-primary hover:underline" data-testid="pkg-uncheck-all">Uncheck all</button>
      </div>
      <div className="rounded-lg border border-border divide-y divide-border">
        {branches.length === 0 && <p className="p-4 text-xs text-muted-foreground text-center">No branches yet.</p>}
        {branches.map((b) => (
          <label key={b.id} className="flex items-center gap-3 p-3 cursor-pointer hover:bg-secondary" data-testid={`pkg-branch-${b.id}`}>
            <Checkbox checked={form.branch_ids.includes(b.id)} onCheckedChange={() => toggle(b.id)} />
            <div className="flex-1">
              <p className="text-sm font-medium">{b.name}</p>
              <p className="text-[11px] text-muted-foreground">{b.address || b.city || ""}</p>
            </div>
          </label>
        ))}
      </div>
      {form.branch_ids.length === 0 && (
        <p className="text-[11px] text-muted-foreground">
          <em>No branches selected — the package will be available at every branch by default.</em>
        </p>
      )}
    </div>
  );
}

/* ---------- Step 3: Package Items ---------- */
// Ordered list of services with per-item substitution options + Move Up /
// Move Down / Delete. The user's Mindbody reference showed a searchable
// combobox — we recreate it with a native Input + filtered dropdown, then
// commit with an "Add Service" button.
const PKG_ITEM_TYPES = [
  { key: "service", label: "Service", add: "Add Service", search: "Type to search services…" },
  { key: "product", label: "Product", add: "Add Product", search: "Type to search products…" },
  { key: "series", label: "Series", add: "Add Series", search: "Type to search series…" },
];

function Step3Items({ form, setForm, services, products, seriesTemplates }) {
  const [tab, setTab] = useState("service");
  const [query, setQuery] = useState("");
  const [picked, setPicked] = useState("");
  const [subOpenIdx, setSubOpenIdx] = useState(null);

  const activeServices = useMemo(() => services.filter((s) => s.active !== false), [services]);
  const activeProducts = useMemo(() => (products || []).filter((p) => p.active !== false), [products]);
  const activeSeries = useMemo(() => (seriesTemplates || []).filter((s) => s.active !== false), [seriesTemplates]);
  const catalogs = useMemo(
    () => ({ service: activeServices, product: activeProducts, series: activeSeries }),
    [activeServices, activeProducts, activeSeries]
  );
  const svcMap = useMemo(() => Object.fromEntries(activeServices.map((s) => [s.id, s])), [activeServices]);
  const refMaps = useMemo(() => ({
    service: svcMap,
    product: Object.fromEntries(activeProducts.map((p) => [p.id, p])),
    series: Object.fromEntries(activeSeries.map((s) => [s.id, s])),
  }), [svcMap, activeProducts, activeSeries]);

  // One-line description under each row / search result, per item type.
  const metaLine = (type, doc) => {
    if (!doc) return "";
    if (type === "service") return `${doc.service_time_min || doc.duration_min || 60} min · ${formatIDR(priceOf(doc))}`;
    if (type === "product") return `${doc.sku ? `SKU ${doc.sku} · ` : ""}${formatIDR(priceOf(doc))}`;
    return `${doc.quantity || 1}× sessions · ${formatIDR(priceOf(doc))}`;
  };

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return (catalogs[tab] || []).filter((s) => (s.name || "").toLowerCase().includes(q)).slice(0, 8);
  }, [query, catalogs, tab]);

  const addItem = () => {
    if (!picked) return;
    if (form.items.some((it) => (it.item_type || "service") === tab && (it.ref_id || it.service_id) === picked)) {
      toast.error(`This ${tab} is already in this package`);
      return;
    }
    setForm({
      ...form,
      items: [...form.items, {
        item_type: tab,
        ref_id: picked,
        service_id: tab === "service" ? picked : null,
        substitutions: [],
      }],
    });
    setPicked(""); setQuery("");
  };
  const removeItem = (idx) => setForm({ ...form, items: form.items.filter((_, i) => i !== idx) });
  const move = (idx, dir) => {
    const next = [...form.items];
    const target = idx + dir;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    setForm({ ...form, items: next });
  };
  const toggleSub = (idx, sid) => {
    const next = [...form.items];
    const cur = new Set(next[idx].substitutions || []);
    if (cur.has(sid)) cur.delete(sid); else cur.add(sid);
    next[idx] = { ...next[idx], substitutions: [...cur] };
    setForm({ ...form, items: next });
  };
  return (
    <div className="space-y-3">
      <div className="rounded-lg border border-sky-200 bg-sky-50 text-sky-900 text-xs p-3 flex gap-2" data-testid="pkg-step3-notice">
        <span className="font-medium">Note:</span>
        <span>If any services in this package are missing at a location they will be added automatically. Check the price settings on Step 4 to ensure they stay correct.</span>
      </div>

      <div className="flex items-center gap-2 text-sm" data-testid="pkg-item-type-tabs">
        {PKG_ITEM_TYPES.map((t, i) => (
          <React.Fragment key={t.key}>
            {i > 0 && <span className="text-muted-foreground">·</span>}
            <button
              type="button"
              onClick={() => { setTab(t.key); setQuery(""); setPicked(""); }}
              className={tab === t.key
                ? "font-medium text-primary underline underline-offset-4"
                : "text-muted-foreground hover:text-foreground transition-colors"}
              data-testid={`pkg-item-type-${t.key}`}
            >
              {t.add}
            </button>
          </React.Fragment>
        ))}
      </div>

      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-md">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={query}
            onChange={(e) => { setQuery(e.target.value); setPicked(""); }}
            placeholder={PKG_ITEM_TYPES.find((t) => t.key === tab)?.search}
            className="pl-8 h-8 text-sm"
            data-testid="pkg-item-search"
          />
          {results.length > 0 && !picked && (
            <div className="absolute top-full left-0 right-0 mt-1 border border-border rounded-md bg-white shadow-lg z-20 max-h-56 overflow-y-auto">
              {results.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => { setPicked(s.id); setQuery(s.name); }}
                  className="w-full text-left px-3 py-2 hover:bg-secondary text-sm border-b border-border last:border-b-0"
                  data-testid={`pkg-item-search-result-${s.id}`}
                >
                  <div>{s.name}</div>
                  <div className="text-[11px] text-muted-foreground">{metaLine(tab, s)}</div>
                </button>
              ))}
            </div>
          )}
        </div>
        <Button size="sm" onClick={addItem} disabled={!picked} data-testid="pkg-add-item-btn">
          {PKG_ITEM_TYPES.find((t) => t.key === tab)?.add}
        </Button>
      </div>

      <div className="rounded-lg border border-border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="h-8 bg-muted/40">
              <TableHead className="py-1.5 text-[11px] uppercase tracking-wider">Name</TableHead>
              <TableHead className="py-1.5 text-[11px] uppercase tracking-wider">Type</TableHead>
              <TableHead className="py-1.5 text-[11px] uppercase tracking-wider text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {form.items.length === 0 && (
              <TableRow>
                <TableCell colSpan={3} className="text-center text-xs text-muted-foreground py-6" data-testid="pkg-items-empty">
                  No items yet — pick Service, Product or Series above, search it, then click Add.
                </TableCell>
              </TableRow>
            )}
            {form.items.map((it, idx) => {
              const type = it.item_type || "service";
              const ref = it.ref_id || it.service_id;
              const s = (refMaps[type] || {})[ref];
              const open = subOpenIdx === idx;
              return (
                <React.Fragment key={type + "-" + ref + "-" + idx}>
                  <TableRow className="h-10 align-top" data-testid={`pkg-item-row-${ref}`}>
                    <TableCell className="py-2 text-sm">
                      <div className="font-medium">{s?.name || ref}</div>
                      <div className="text-[11px] text-muted-foreground">{metaLine(type, s)}</div>
                    </TableCell>
                    <TableCell className="py-2 text-xs text-muted-foreground" data-testid={`pkg-item-type-cell-${ref}`}>
                      {PKG_ITEM_TYPES.find((t) => t.key === type)?.label || "Service"}
                    </TableCell>
                    <TableCell className="py-2 text-right">
                      <div className="inline-flex items-center gap-3 text-xs">
                        {type === "service" && (
                          <button type="button" onClick={() => setSubOpenIdx(open ? null : idx)} className="text-primary hover:underline inline-flex items-center gap-1" data-testid={`pkg-item-sub-${ref}`}>
                            <GitBranch className="w-3.5 h-3.5" /> Substitutions
                          </button>
                        )}
                        <button type="button" onClick={() => move(idx, -1)} disabled={idx === 0} className="text-primary hover:underline disabled:opacity-40 inline-flex items-center gap-1" data-testid={`pkg-item-up-${ref}`}>
                          <ArrowUp className="w-3.5 h-3.5" /> Move Up
                        </button>
                        <button type="button" onClick={() => move(idx, 1)} disabled={idx === form.items.length - 1} className="text-primary hover:underline disabled:opacity-40 inline-flex items-center gap-1" data-testid={`pkg-item-down-${ref}`}>
                          <ArrowDown className="w-3.5 h-3.5" /> Move Down
                        </button>
                        <button type="button" onClick={() => removeItem(idx)} className="text-destructive hover:underline inline-flex items-center gap-1" data-testid={`pkg-item-delete-${ref}`}>
                          <Trash2 className="w-3.5 h-3.5" /> Delete
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                  {open && type === "service" && (
                    <TableRow className="bg-muted/30" data-testid={`pkg-item-sub-panel-${ref}`}>
                      <TableCell colSpan={3} className="py-3">
                        <SubstitutionsPicker
                          services={activeServices.filter((s2) => s2.id !== ref)}
                          selected={it.substitutions || []}
                          onToggle={(sid) => toggleSub(idx, sid)}
                          parentId={ref}
                        />
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function SubstitutionsPicker({ services, selected, onToggle, parentId }) {
  const [q, setQ] = useState("");
  const [picked, setPicked] = useState("");
  const results = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return [];
    return services.filter((s) => s.name.toLowerCase().includes(needle) && !selected.includes(s.id)).slice(0, 8);
  }, [q, services, selected]);
  const svcMap = useMemo(() => Object.fromEntries(services.map((s) => [s.id, s])), [services]);

  const add = () => { if (picked) { onToggle(picked); setPicked(""); setQ(""); } };

  return (
    <div className="pl-6 space-y-2">
      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-md">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => { setQ(e.target.value); setPicked(""); }}
            placeholder="Search substitutable service…"
            className="pl-8 h-8 text-sm"
            data-testid={`pkg-sub-search-${parentId}`}
          />
          {results.length > 0 && !picked && (
            <div className="absolute top-full left-0 right-0 mt-1 border border-border rounded-md bg-white shadow-lg z-20 max-h-56 overflow-y-auto">
              {results.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => { setPicked(s.id); setQ(s.name); }}
                  className="w-full text-left px-3 py-2 hover:bg-secondary text-sm border-b border-border last:border-b-0"
                  data-testid={`pkg-sub-result-${parentId}-${s.id}`}
                >
                  {s.name}
                </button>
              ))}
            </div>
          )}
        </div>
        <Button size="sm" onClick={add} disabled={!picked} data-testid={`pkg-sub-add-${parentId}`}>Add Service</Button>
      </div>
      {selected.length > 0 && (
        <div className="text-xs">
          <p className="text-muted-foreground mb-1">May be substituted with:</p>
          <ul className="space-y-1">
            {selected.map((sid) => (
              <li key={sid} className="flex items-center gap-2" data-testid={`pkg-sub-item-${parentId}-${sid}`}>
                <span className="text-foreground">{svcMap[sid]?.name || sid}</span>
                <button type="button" onClick={() => onToggle(sid)} className="text-primary hover:underline" data-testid={`pkg-sub-remove-${parentId}-${sid}`}>remove</button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/* ---------- Step 4: Pricing ---------- */
function Step4Pricing({ form, setForm, branches, services, products, seriesTemplates }) {
  const p = form.pricing || { same_for_all: true, price: 0, per_branch: {} };
  const setSame = (v) => setForm({ ...form, pricing: { ...p, same_for_all: v } });
  const setPrice = (v) => setForm({ ...form, pricing: { ...p, price: v } });
  const setBranchPrice = (bid, k, v) => setForm({
    ...form,
    pricing: {
      ...p,
      per_branch: { ...p.per_branch, [bid]: { ...(p.per_branch[bid] || {}), [k]: v } },
    },
  });
  // Only show branches selected on Step 2 (or all branches if none selected).
  const visible = form.branch_ids.length ? branches.filter((b) => form.branch_ids.includes(b.id)) : branches;

  // À la carte total from the package's ordered items — this is the "original"
  // price the client would have paid buying each service separately. The
  // operator uses this to decide how deep the package discount should be.
  const svcMap = useMemo(() => Object.fromEntries((services || []).map((s) => [s.id, s])), [services]);
  const refMaps = useMemo(() => ({
    service: svcMap,
    product: Object.fromEntries((products || []).map((p) => [p.id, p])),
    series: Object.fromEntries((seriesTemplates || []).map((s) => [s.id, s])),
  }), [svcMap, products, seriesTemplates]);
  const originalTotal = useMemo(
    () => form.items.reduce((sum, it) => {
      const type = it.item_type || "service";
      const doc = (refMaps[type] || {})[it.ref_id || it.service_id];
      return sum + priceOf(doc);
    }, 0),
    [form.items, refMaps]
  );
  const discountFor = (packagePrice) => {
    const pp = Number(packagePrice || 0);
    if (!originalTotal || pp <= 0) return null;
    const pct = ((originalTotal - pp) / originalTotal) * 100;
    // Round to 1 decimal; drop trailing zero for clean labels.
    const rounded = Math.round(pct * 10) / 10;
    return rounded;
  };

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-border p-3">
        <div className="flex items-center justify-between">
          <div>
            <Label className="text-sm">Same price for all locations</Label>
            <p className="text-[11px] text-muted-foreground mt-0.5">Off → set a unique price and currency per branch.</p>
          </div>
          <Switch checked={p.same_for_all} onCheckedChange={setSame} data-testid="pkg-same-price-switch" />
        </div>
      </div>

      {/* À la carte reference — always shown so the operator sees the baseline
          price they're discounting against. Falls back to a helpful hint when
          Step 3 has no items yet. */}
      <div className="rounded-lg border border-border bg-muted/30 p-3 text-sm flex flex-wrap items-baseline gap-2" data-testid="pkg-alacarte-summary">
        <span className="text-muted-foreground">Original price (à la carte total):</span>
        <span className="font-medium text-foreground" data-testid="pkg-alacarte-total">{formatIDR(originalTotal)}</span>
        {originalTotal === 0 && (
          <span className="text-[11px] text-muted-foreground">— add services on Step 3 to see the baseline.</span>
        )}
      </div>

      {p.same_for_all ? (
        <div>
          <Label>Package price (IDR)</Label>
          <div className="mt-1 flex items-center gap-3 flex-wrap">
            <Input type="number" value={p.price} onChange={(e) => setPrice(e.target.value)} className="max-w-xs" data-testid="pkg-price-input" />
            <DiscountBadge pct={discountFor(p.price)} testid="pkg-discount-badge" />
          </div>
        </div>
      ) : (
        <div className="rounded-lg border border-border divide-y divide-border">
          {visible.length === 0 && <p className="p-4 text-xs text-muted-foreground text-center">Select at least one branch on Step 2.</p>}
          {visible.map((b) => {
            const pb = p.per_branch?.[b.id] || {};
            return (
              <div key={b.id} className="p-3 flex items-center gap-3 flex-wrap" data-testid={`pkg-branch-price-${b.id}`}>
                <div className="flex-1 min-w-[160px]">
                  <p className="text-sm font-medium">{b.name}</p>
                  <p className="text-[11px] text-muted-foreground">{b.address || b.city || ""}</p>
                </div>
                <Input
                  type="number"
                  placeholder="Price"
                  value={pb.price ?? ""}
                  onChange={(e) => setBranchPrice(b.id, "price", e.target.value)}
                  className="w-32 h-8 text-sm"
                  data-testid={`pkg-branch-price-input-${b.id}`}
                />
                <Select value={pb.currency || b.currency || "IDR"} onValueChange={(v) => setBranchPrice(b.id, "currency", v)}>
                  <SelectTrigger className="w-24 h-8 text-xs" data-testid={`pkg-branch-currency-${b.id}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(CURRENCY_OPTIONS || [{ code: "IDR", label: "IDR" }, { code: "USD", label: "USD" }]).map((c) => (
                      <SelectItem key={c.code} value={c.code}>{c.code}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <DiscountBadge pct={discountFor(pb.price)} testid={`pkg-branch-discount-${b.id}`} />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Small colour-coded pill that expresses the package discount at a glance.
// Positive = savings; negative would mean the package is priced ABOVE the
// à-la-carte sum, which is almost always a data-entry mistake — we flag it
// in red so the operator notices.
function DiscountBadge({ pct, testid }) {
  if (pct === null) return null;
  const fmt = (n) => (Number.isInteger(n) ? `${n}` : n.toFixed(1));
  if (pct > 0) {
    return (
      <span
        className="inline-flex items-center rounded-full bg-emerald-100 text-emerald-700 border border-emerald-200 px-2 py-0.5 text-[11px] font-medium"
        data-testid={testid}
        title={`Package saves ${fmt(pct)}% vs à la carte`}
      >
        {fmt(pct)}% off
      </span>
    );
  }
  if (pct < 0) {
    return (
      <span
        className="inline-flex items-center rounded-full bg-rose-100 text-rose-700 border border-rose-200 px-2 py-0.5 text-[11px] font-medium"
        data-testid={testid}
        title="Package price is higher than à-la-carte — please check"
      >
        {fmt(Math.abs(pct))}% above
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center rounded-full bg-muted text-muted-foreground border border-border px-2 py-0.5 text-[11px]"
      data-testid={testid}
    >
      no discount
    </span>
  );
}

// Local helper — computed inside table cells where svcMap is in scope; here for step 3.
function priceOf(s) { return Number(s?.pricing?.price ?? s?.price ?? 0); }
